import React, {ReactNode, useCallback, useMemo} from "react";
import {useFieldControl} from "../control";
import {Checkbox, MenuItem, TextField, TextFieldProps} from "@mui/material";

export interface FieldSelectOption<
    Value,
    Multiple extends boolean | undefined = false,
> {
    key: string | number,
    label: ReactNode,
    value: FieldSelectValue<Value, Multiple>,
    onSelect?: () => void,
}

export type FieldSelectValue<Value, Multiple> = Multiple extends true
    ? Value extends Array<infer Inner> ? Inner : never
    : Value;

export interface FieldSelectProps<Value, Multiple extends boolean | undefined> {
    limitTags?: number;
    multiple?: Multiple;
    autoCommit?: boolean;
    options: Array<FieldSelectOption<Value, Multiple>>
    isOptionEqualToValue?: (option: FieldSelectOption<Value, Multiple>, value: FieldSelectValue<Value, Multiple>) => boolean;
}

const FieldSelect = <
    Value,
    Multiple extends boolean = false,
>(props: FieldSelectProps<Value, Multiple>) => {
    type PropType = FieldSelectProps<Value, Multiple>;
    const {isOptionEqualToValue: isOptionEqualToValueProps} = props;

    const fieldControl = useFieldControl<FieldSelectValue<Value, Multiple>>();
    const {setValue, saveChanges} = fieldControl;

    const limitTags = useMemo(() => props.limitTags ?? 2, [props.limitTags]);

    /**
     * Determines whether the given option is equal to the given value.
     */
    const isOptionEqualToValue = useCallback<Required<PropType>['isOptionEqualToValue']>((option, value) =>
            !!isOptionEqualToValueProps
                ? isOptionEqualToValueProps(option, value)
                : option.value === value
        , [isOptionEqualToValueProps]);

    /**
     * Renders the value of the select.
     */
    const renderValue = useCallback((value: unknown) => {
        if (props.multiple) {
            const valueList = value as Array<any>;
            const extraTags = valueList.length - limitTags;
            return valueList
                .filter((_, index) => index < limitTags)
                .map<ReactNode>((v) => props.options.find(e => isOptionEqualToValue(e, v))?.label ?? "")
                .join(", ")
                .concat(extraTags > 0 ? ` +${extraTags}` : "");
        }
        return props.options.find(e => isOptionEqualToValue(e, value as any))?.label ?? "";
    }, [isOptionEqualToValue, limitTags, props.multiple, props.options]);

    /**
     * Calls the [onSelect] callback of the option and prevents the default behavior of the event only if the option has an [onSelect] callback.
     */
    const onOptionClicked = useCallback((event: React.MouseEvent<HTMLElement>, option: FieldSelectOption<Value, Multiple>) => {
        if (option.onSelect) {
            event.stopPropagation();
            event.preventDefault();
            option.onSelect();
        }
    }, []);

    /**
     * Only calls the [setValue] callback if the option does not have an [onSelect] callback.
     */
    const onChange = useCallback<Required<TextFieldProps>['onChange']>((e) => {
        if (props.multiple) {
            const valueList = e.target.value as unknown as Array<any>;
            for (const value of e.target.value as any) {
                const option = props.options.find(o => isOptionEqualToValue(o, value));
                if (option?.onSelect) {
                    valueList.splice(valueList.indexOf(value), 1);
                }
            }
            return setValue(valueList as any);
        } else {
            const option = props.options.find(o => isOptionEqualToValue(o, e.target.value as any));
            if (option && !option.onSelect) {
                setValue(e.target.value as any)
            }
        }
    }, [isOptionEqualToValue, props.multiple, props.options, setValue]);

    /**
     * Handles the key up event.
     * - If the user presses enter, the changes are saved immediately.
     */
    const onKeyUp = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        switch (e.key) {
            case "Enter":
                e.target.dispatchEvent(new Event('blur'));
                setTimeout(() => void saveChanges(), 100)
                break;
        }
    }, [saveChanges])

    const optionElements = useMemo(() => props.options.map((option) => {
        let checked = false;
        if (props.multiple && !option.onSelect) {
            const valueList = (fieldControl.value ?? []) as Array<any>;
            checked = valueList.find(v => isOptionEqualToValue(option, v)) !== undefined;
        }
        return (
            <MenuItem
                key={option.key}
                value={option.value as any}
                onClick={(e) => onOptionClicked(e, option)}
            >
                {
                    props.multiple && !option.onSelect &&
                    <Checkbox checked={checked}/>
                }
                {option.label}
            </MenuItem>
        )
    }), [props.options, props.multiple, fieldControl.value, isOptionEqualToValue, onOptionClicked])

    return (
        <>
            <TextField
                select
                fullWidth
                variant={'outlined'}
                value={props.multiple ? (fieldControl.value ?? []) : (fieldControl.value ?? "")}
                onChange={onChange}
                onBlur={async (e) => {
                    onChange(e);
                    if (props.autoCommit ?? true) {
                        await saveChanges();
                    }
                }}
                onKeyUp={onKeyUp}
                disabled={fieldControl.disabled}
                helperText={fieldControl.helperText}
                error={fieldControl.erroneous}
                sx={fieldControl.sx}
                InputProps={{
                    readOnly: fieldControl.readonly,
                }}
                SelectProps={{
                    multiple: props.multiple,
                    renderValue: renderValue,
                }}
            >
                {optionElements}
            </TextField>
        </>
    );
}

export default FieldSelect;
